import React, { useCallback, useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import { ResultsSummary } from "./results-summary";
import { ResultsPerPage } from "./results-per-page";
import { PaginationPages } from "./pagination-pages";
import { CustomResultsSummaryFn, PaginationProps } from "./pagination.types";
import { PaginationTruncatedPages } from "./pagination-truncated-pages";

const defaultCustomResultsSummaryFn = ({
  from,
  to,
  count,
  resultsLabel,
  resultLabel,
  ofTextLabel,
}: CustomResultsSummaryFn): string =>
  `${from}-${to} ${ofTextLabel} ${count} ${
    count > 1 ? resultsLabel : resultLabel
  }`;

/**
 * Container of Pagination. The pagination is broken down to 3 parts out of which
 * 1. Pagination (rendered always)
 * 2. Results per page (can be turned off by a prop)
 * 3. Results summary (can be turned off by a prop)
 *
 * Pagination component does not need to know about the structure or contents of
 * the data that gets handled. Pagination needs the size of resultSet at the minimum.
 *
 * The developer need to handle the data that gets displayed with PageNumber
 * and PageSize information exposed by the Pagination component.
 */
export const Pagination = ({
  /* Required */
  resultsCount,

  /* Optional */
  ariaItemLabel = "Go to page",
  className,
  customResultsSummaryFn = defaultCustomResultsSummaryFn,
  isTruncated,
  navAriaLabel = "Navigation",
  nextAriaLabel = "Next page",
  nextLabel = "Next",
  numberOfPagesToDisplay = 5,
  ofTextLabel = "of",
  onPageClick,
  onPageSizeChange,
  onResultsPerPageChange,
  page: presetPage = 1,
  pageSize: pageSizeProp = 10,
  previousAriaLabel = "Previous page",
  previousLabel = "Previous",
  resultSingularLabel = "result",
  resultsLabel = "results",
  resultsPerPageOptions = [10, 25, 50, 100],
  resultsTitleLabel = "Results Per Page",
  showResultsPerPage = true,
  showResultsSummary = true,

  /* Developers may pass ID or any other nav attributes, so we are storing them in
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                          rest variable and later set to container nav tag*/
  ...rest
}: PaginationProps): React.ReactElement | null => {
  const [pageSize, setPageSize] = useState(pageSizeProp);
  const [page, setPage] = useState(presetPage || 1);

  let shouldRenderTruncated = false;

  // Check if both resultsCount and pageSize is valid and not zero
  const isValidResultsAndPageSize = !!resultsCount && !!pageSize;
  if (isValidResultsAndPageSize && isTruncated) {
    if (resultsCount / pageSize > 5) {
      shouldRenderTruncated = true;
    }
  }

  useEffect(() => {
    setPage(presetPage);
  }, [presetPage]);

  /*
   * When number of results per page is changed in the dropdown,
   * we set page to 1, and re-render the data.
   * Developers may extend the functionality with redux action or callback to fetch API results
   */
  const pageSizeChangeHandler = useCallback(
    (newPageSizeFromDropdown: string): void => {
      const newPageSize = Number(newPageSizeFromDropdown);
      if (isNaN(newPageSize)) {
        console.warn(
          `pageSize must a valid number, invalid page-size ${newPageSizeFromDropdown} encountered`
        );
        return;
      }
      setPageSize(newPageSize);
      setPage(1);
      onPageSizeChange?.(newPageSizeFromDropdown);
      onResultsPerPageChange?.(Number(newPageSizeFromDropdown));
      onPageClick?.(0, 1);
    },
    [onPageClick, onPageSizeChange, onResultsPerPageChange]
  );

  /*
   * When user clicks on the page numbers that are displayed, we need to show the data
   * corresponding to that page. Developers can extend this method with a callback
   * to do API calls or to do client side pagination.
   */
  const _pageClickHandler = (pageNumber: number): void => {
    setPage(pageNumber);
    onPageClick?.(pageNumber - 1, pageNumber);
  };

  /*
   * Method to make render compact and more readable
   */
  const renderResultsPerPage = useMemo(
    (): React.ReactElement => (
      <ResultsPerPage
        resultsCount={resultsCount}
        pageSize={pageSize}
        resultsPerPageOptions={resultsPerPageOptions}
        resultsLabel={resultsLabel}
        resultsTitleLabel={resultsTitleLabel}
        resultSingularLabel={resultSingularLabel}
        onPageSizeChange={pageSizeChangeHandler}
      />
    ),
    [
      pageSize,
      pageSizeChangeHandler,
      resultSingularLabel,
      resultsCount,
      resultsLabel,
      resultsPerPageOptions,
      resultsTitleLabel,
    ]
  );

  /*
   * Method to make render compact and more readable
   */
  const renderResultsSummary = useMemo(
    (): React.ReactElement => (
      <ResultsSummary
        pageSize={pageSize}
        startIndex={(page - 1) * (pageSize || 10)}
        resultsCount={resultsCount}
        generateRowTextFn={customResultsSummaryFn}
        resultsLabel={resultsLabel}
        resultLabel={resultSingularLabel}
        ofTextLabel={ofTextLabel}
      />
    ),
    [
      customResultsSummaryFn,
      ofTextLabel,
      page,
      pageSize,
      resultSingularLabel,
      resultsCount,
      resultsLabel,
    ]
  );

  /*
   * When custom page prop is sent, check if results are available for display
   */
  if (presetPage > 1) {
    const resultsStartIndex = pageSizeProp * (page - 1);
    if (resultsStartIndex > resultsCount) {
      console.error("Check props page, resultsCount. There could be an error");
      return null;
    }
  }

  /*
   * When there are no results to show, then we do not render the component
   */
  if (resultsCount === 0) {
    console.info("Results count is zero, pagination component will not render");
    return null;
  }

  const attrs = {
    page,
    setPage,
    resultsCount,
    pageSize,
    previousLabel,
    previousAriaLabel,
    nextLabel,
    nextAriaLabel,
    ariaItemLabel,
    pageClickHandler: _pageClickHandler,
    numberOfPagesToDisplay,
  };

  /*
   * Developer can pass in className, id or aria-label to the nav tag only
   *
   * By passing pageSize and resultsPerPageOptions, developer can change the
   * default option that gets displayed
   *
   * By passing in the page prop, developer can decide to show particular
   * page data otherwise the pagination components starts with page 1. This is
   * useful when the page number is deeplinked to URL.
   */
  return (
    <nav
      className={classnames("spark-pagination", className)}
      aria-label={navAriaLabel}
      {...rest}
    >
      {shouldRenderTruncated ? (
        <PaginationTruncatedPages {...attrs} />
      ) : (
        <PaginationPages {...attrs} />
      )}
      <div className="spark-pagination__results">
        {showResultsPerPage && renderResultsPerPage}
        {showResultsSummary && renderResultsSummary}
      </div>
    </nav>
  );
};

Pagination.displayName = "Pagination";
